import styled from "styled-components";
import { Link } from "react-router-dom";
import { WrongLocationOutlined } from "@mui/icons-material";
import theme from "../styles/theme";
import NotFoundSEO from "../components/SEO/NotFoundSEO";

// The catch-all page, and the fallback for entities that don't exist (an unknown driver id).
// Amplify serves index.html with HTTP 200 for any unmatched path, so without this route React
// rendered an empty shell and Google indexed the result — that is how /teams/ got in.
//
// It renders inside App.js's GlobalContainer, so the header and footer are already there. That
// matters beyond looks: those nav links are how a crawler that landed here finds its way back.
//
// Styling follows DriverStatsEmpty — checkered strip over a dashed box — so a dead end looks
// like the rest of the site rather than a browser error.

const Container = styled.div`
  margin-top: 24px;
  margin-bottom: 32px;
`;

const CheckeredStrip = styled.div`
  height: 10px;
  background-image:
    linear-gradient(45deg, ${theme.colors.secondaryDark} 25%, transparent 25%),
    linear-gradient(-45deg, ${theme.colors.secondaryDark} 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, ${theme.colors.secondaryDark} 75%),
    linear-gradient(-45deg, transparent 75%, ${theme.colors.secondaryDark} 75%);
  background-size: 12px 12px;
  background-position:
    0 0,
    0 6px,
    6px -6px,
    -6px 0;
`;

const PitBox = styled.div`
  border: 2px dashed ${theme.colors.secondaryDark};
  border-top: none;
  border-radius: 0 0 4px 4px;
  background: #2a2a2a66;
  padding: 56px 24px 52px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 14px;
`;

const PitIcon = styled.div`
  color: ${theme.colors.secondaryDark};
  display: flex;

  & .MuiSvgIcon-root {
    font-size: 64px;
  }
`;

const ErrorCode = styled.div`
  font-family: "Roboto Condensed", sans-serif;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.18em;
  color: ${theme.colors.grey};
`;

const NotFoundTitle = styled.h1`
  font-family: "Montserrat", sans-serif;
  font-size: 28px;
  font-weight: 700;
  color: ${theme.colors.white};
  margin: 0;

  @media (max-width: ${theme.breakpointsCustom.mobile}px) {
    font-size: 22px;
  }
`;

const NotFoundText = styled.div`
  font-family: "Montserrat", sans-serif;
  font-size: 14px;
  line-height: 1.55;
  color: ${theme.colors.grey};
  max-width: 460px;
`;

const Links = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  margin-top: 10px;
`;

const RecoveryLink = styled(Link)`
  font-family: "Montserrat", sans-serif;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  text-decoration: none;
  color: ${theme.colors.green};
  background: transparent;
  border: 2px solid ${theme.colors.green};
  border-radius: 4px;
  padding: 9px 18px;
  transition:
    background-color 0.18s ease,
    color 0.18s ease;

  &:hover {
    background: ${theme.colors.green};
    color: ${theme.colors.dark};
  }
`;

const recoveryLinks = [
  { title: "Головна", link: "/" },
  { title: "Новини", link: "/news" },
  { title: "Результати", link: "/results" },
  { title: "Пілоти", link: "/drivers" },
  { title: "Зала Слави", link: "/hall-of-fame" },
];

const NotFound = ({
  title = "Сторінку не знайдено",
  text = "Схоже, ви з'їхали з траси — такої сторінки не існує або вона переїхала. Спробуйте повернутися на один із розділів нижче.",
}) => (
  <>
    <NotFoundSEO title={title} />
    <Container>
      <CheckeredStrip />
      <PitBox>
        <PitIcon>
          <WrongLocationOutlined />
        </PitIcon>
        <ErrorCode>ПОМИЛКА 404</ErrorCode>
        <NotFoundTitle>{title}</NotFoundTitle>
        <NotFoundText>{text}</NotFoundText>
        <Links>
          {recoveryLinks.map(({ title: linkTitle, link }) => (
            <RecoveryLink key={link} to={link}>
              {linkTitle}
            </RecoveryLink>
          ))}
        </Links>
      </PitBox>
    </Container>
  </>
);

export default NotFound;
